import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../services/api';
import {
  Shirt, Link2, CalendarDays, Settings, ClipboardList,
  GraduationCap, BookOpen, Info, Palmtree, ArrowLeft
} from 'lucide-react';

const ICON_MAP = {
  shirt: Shirt, link: Link2, calendar: CalendarDays,
  settings: Settings, clipboard: ClipboardList,
  graduation: GraduationCap, book: BookOpen, info: Info,
};

const DEFAULT_LINKS = [
  { slug: 'uniformes',    name: 'Solicitud de Uniformes', url: 'https://www.uniformes-mcd.es/', icon_name: 'shirt',      order: 1 },
  { slug: 'mcdconnect',   name: 'McD Connect',            url: 'http://api.joinblink.com/go/mcd-es', icon_name: 'link', order: 2 },
  { slug: 'orquest',      name: 'Orquest',                url: 'https://play.google.com/store/apps/details?id=es.decide.mobile.orquest&hl=es', icon_name: 'calendar', order: 3 },
  { slug: 'cursos',       name: 'Cursos',                 url: 'https://mcdcampus.sabacloud.com/', icon_name: 'graduation', order: 4 },
];

// Aplicaciones con una dirección distinta en iPhone. El enlace guardado lleva a
// Google Play, que en un iPhone no sirve; ahí se abre la App Store.
const ENLACE_IPHONE = {
  orquest: 'https://apps.apple.com/es/app/orquest/id1453532559',
};
const esIphone = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

// El botón de solicitud de vacaciones no se usa por ahora. Se oculta sin
// eliminar su lógica: basta con volver a poner esto a true.
const MOSTRAR_VACACIONES = false;

export default function LinksPage() {
  const navigate = useNavigate();
  const [apiLinks, setApiLinks] = useState([]);
  const [appSettings, setAppSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      publicApi.getLinks().catch(() => ({ data: [] })),
      publicApi.getSettings().catch(() => ({ data: {} })),
    ]).then(([linksRes, settingsRes]) => {
      setApiLinks(Array.isArray(linksRes.data) ? linksRes.data : []);
      setAppSettings(settingsRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner" style={{ marginTop: 80 }} />;

  // La API es la fuente de verdad. Solo usamos DEFAULT_LINKS como respaldo
  // si la API no devuelve nada (sin conexión o error).
  // Se excluyen los enlaces marcados como "solo en el alta" (por ejemplo, el
  // formulario de vacaciones), que se gestionan desde el mismo panel pero no
  // deben aparecer como botón aquí.
  const visibleApiLinks = apiLinks.filter(l => l.show_in_repository !== false);
  const mergedLinks = (visibleApiLinks.length > 0 ? [...visibleApiLinks] : [...DEFAULT_LINKS]);
  mergedLinks.sort((a, b) => (a.order || 0) - (b.order || 0));

  const vacationEnabled = appSettings?.vacation_requests_enabled !== false;

  return (
    <div className="page-content" style={{ maxWidth: 900 }}>
      <button onClick={() => navigate('/')} className="btn btn--ghost" style={{ marginBottom: 20 }}>
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="page-header animate-in">
        <h1>Repositorio de Enlaces</h1>
        <p>Acceso directo a todas las herramientas y plataformas</p>
      </div>

      <div className="services-grid">
        {mergedLinks.map((link, i) => {
          const IconComp = ICON_MAP[link.icon_name] || Info;
          return (
            <a
              key={i}
              href={(esIphone() && ENLACE_IPHONE[link.slug]) || link.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="service-card animate-in"
            >
              <div className="service-card__icon">
                <IconComp size={26} />
              </div>
              <span className="service-card__label">{link.name}</span>
            </a>
          );
        })}

        {/* Vacation button */}
        {MOSTRAR_VACACIONES && (
        <a
          href={vacationEnabled ? '/vacaciones' : undefined}
          onClick={e => {
            e.preventDefault();
            if (vacationEnabled) navigate('/vacaciones');
          }}
          className={`service-card animate-in ${!vacationEnabled ? 'service-card--disabled' : ''}`}
        >
          <div className="service-card__icon service-card__icon--vacation">
            <Palmtree size={26} />
          </div>
          <span className="service-card__label">
            {appSettings?.vacation_button_text || 'Solicitud de Vacaciones'}
          </span>
          {!vacationEnabled && (
            <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>No disponible</span>
          )}
        </a>
        )}
      </div>
    </div>
  );
}
